import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { format } from 'date-fns';
import './JobCard.css';

// Simple inline SVG icons — no external dependency
const CalendarIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const LoaderIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="2" x2="12" y2="6" />
    <line x1="12" y1="18" x2="12" y2="22" />
    <line x1="4.93" y1="4.93" x2="7.76" y2="7.76" />
    <line x1="16.24" y1="16.24" x2="19.07" y2="19.07" />
    <line x1="2" y1="12" x2="6" y2="12" />
    <line x1="18" y1="12" x2="22" y2="12" />
    <line x1="4.93" y1="19.07" x2="7.76" y2="16.24" />
    <line x1="16.24" y1="7.76" x2="19.07" y2="4.93" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const XCircleIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="15" y1="9" x2="9" y2="15" />
    <line x1="9" y1="9" x2="15" y2="15" />
  </svg>
);

const statusConfig = {
  scheduled:  { color: '#f59e0b', icon: CalendarIcon, label: 'Scheduled' },
  'in-progress': { color: '#3b82f6', icon: LoaderIcon, label: 'In Progress' },
  completed:  { color: '#10b981', icon: CheckCircleIcon, label: 'Completed' },
  cancelled:  { color: '#ef4444', icon: XCircleIcon, label: 'Cancelled' },
};

const JobCard = ({ job, onStatusChange, onDelete }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/jobs/${job.id}`);
  };

  const handleStatusClick = (e) => {
    e.stopPropagation();
    const next =
      job.status === 'scheduled'
        ? 'in-progress'
        : job.status === 'in-progress'
        ? 'completed'
        : 'scheduled';
    onStatusChange?.(job.id, next);
  };

  // Handle both single and multiple workers
  const workers = Array.isArray(job.assignedToName)
    ? job.assignedToName.filter(Boolean)
    : job.assignedToName
    ? [job.assignedToName]
    : [];

  return (
    <div className="job-card" onClick={handleClick} role="button" tabIndex={0}>
      <div className="job-card-header">
        <h3 className="job-title">{job.title}</h3>
        <div className="job-card-actions">
          <span
            className={`job-status-badge ${job.status === 'in-progress' ? 'status-spinning' : ''}`}
            style={{ backgroundColor: statusConfig[job.status]?.color || '#6b7280' }}
            onClick={handleStatusClick}
          >
            {(() => {
              const StatusIcon = statusConfig[job.status]?.icon;
              return StatusIcon ? <StatusIcon size={13} /> : null;
            })()}
            {statusConfig[job.status]?.label || job.status}
          </span>
          {onDelete && (
            <button
              className="job-delete-btn"
              onClick={(e) => { e.stopPropagation(); if (window.confirm('Are you sure you want to delete this job?')) onDelete(job.id); }}
              aria-label="Delete job"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* ─── Zoho Company Info ─── */}
      {job.companyName && (
        <p className="job-company">
          🏢 <strong>{job.companyName}</strong>
          {job.jobReference && <span className="job-ref"> • Ref: {job.jobReference}</span>}
        </p>
      )}

      {/* ─── Client & Site Contact ─── */}
      <div className="job-contacts">
        {job.clientContact && (
          <span className="job-contact-chip">👤 {job.clientContact}</span>
        )}
        {job.primarySiteContact && (
          <span className="job-contact-chip">🏗️ {job.primarySiteContact}</span>
        )}
        {job.jobType && (
          <span className="job-type-chip">🔧 {job.jobType}</span>
        )}
      </div>

      {/* ─── Location & Date ─── */}
      <p className="job-location">
        📍{' '}
        <span
          className="job-location-link"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/jobs/location/${encodeURIComponent(job.location || '')}`);
          }}
        >
          {job.location}
        </span>
      </p>
      <p className="job-datetime">
        📅 {job.scheduledDate ? format(new Date(job.scheduledDate), 'MMM d, yyyy h:mm a') : 'No date'}
      </p>
      
      {/* ─── Multiple Workers ─── */}
      {workers.length > 0 && (
        <div className="job-workers">
          {workers.map((name, i) => {
            const workerUid = Array.isArray(job.assignedTo) ? job.assignedTo[i] : job.assignedTo;
            return (
              <span
                key={i}
                className="job-worker-chip"
                onClick={(e) => {
                  e.stopPropagation();
                  if (workerUid) {
                    navigate(`/jobs/worker/${workerUid}?name=${encodeURIComponent(name)}`);
                  }
                }}
              >
                👷 {name}
              </span>
            );
          })}
        </div>
      )}
      
      <p className="job-desc">
        {job.jobDetails?.slice(0, 120) || job.description?.slice(0, 120)}
        {(job.jobDetails?.length > 120 || job.description?.length > 120) ? '...' : ''}
      </p>

      {/* ─── Photo Count ─── */}
      {(job.photoCount > 0 || job.jobPhotos?.length > 0) && (
        <p className="job-photos">
          📸 {job.photoCount || job.jobPhotos?.length || 0} photo(s)
        </p>
      )}

      {/* ─── Zoho Sync Badge ─── */}
      {job.zohoSynced && (
        <span className="zoho-sync-badge">🔗 Zoho</span>
      )}
    </div>
  );
};

export default JobCard;
